import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { configureClientErrorReporting } from '@frontend-shared/http'
import type { JwtPayload } from '@frontend-shared/auth/types'

import { clearApiCache, setAuthRejectedHandler } from '../api/client'
import { syncTelemetryUser } from '../telemetry'
import { AuthContext } from './AuthContextValue'
import type { PortalProfile } from './types'

const STORAGE_TOKEN = 'portalToken'
const STORAGE_PROFILE = 'portalProfile'
const STORAGE_BRIDGE = 'portalAuthBridge'
const AUTH_BRIDGE_VERSION = 1
const AUTH_BRIDGE_TTL_MS = 15 * 60 * 1000
const AUTH_BRIDGE_HEARTBEAT_MS = 60 * 1000

interface AuthState {
  token: string | null
  profile: PortalProfile | null
}

interface AuthBridgePayload {
  version: number
  token: string
  profile: PortalProfile | null
  expiresAt: number
  updatedAt: number
}

const decodeJwtPayload = (token: string | null): JwtPayload | null => {
  if (!token) {
    return null
  }

  const parts = token.split('.')
  if (parts.length < 2 || !parts[1]) {
    return null
  }

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as JwtPayload
  } catch {
    return null
  }
}

const isTokenExpired = (token: string | null): boolean => {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') {
    return false
  }
  return payload.exp * 1000 <= Date.now()
}

const clearSessionAuth = () => {
  sessionStorage.removeItem(STORAGE_TOKEN)
  sessionStorage.removeItem(STORAGE_PROFILE)
}

const clearBridgeAuth = () => {
  localStorage.removeItem(STORAGE_BRIDGE)
}

const clearStoredAuth = () => {
  clearSessionAuth()
  clearBridgeAuth()
}

const readToken = (): string | null => {
  const token = sessionStorage.getItem(STORAGE_TOKEN)
  if (!token) {
    return null
  }
  if (isTokenExpired(token)) {
    clearSessionAuth()
    return null
  }
  return token
}

const readProfile = (): PortalProfile | null => {
  const raw = sessionStorage.getItem(STORAGE_PROFILE)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as PortalProfile
  } catch {
    return null
  }
}

const writeSessionAuth = (token: string | null, profile: PortalProfile | null) => {
  if (token) {
    sessionStorage.setItem(STORAGE_TOKEN, token)
  } else {
    sessionStorage.removeItem(STORAGE_TOKEN)
  }

  if (profile) {
    sessionStorage.setItem(STORAGE_PROFILE, JSON.stringify(profile))
  } else {
    sessionStorage.removeItem(STORAGE_PROFILE)
  }
}

const createBridgePayload = (
  token: string | null,
  profile: PortalProfile | null,
  nowMs = Date.now()
): AuthBridgePayload | null => {
  if (!token) {
    return null
  }

  const payload = decodeJwtPayload(token)
  const tokenExpiresAt = payload && typeof payload.exp === 'number' ? payload.exp * 1000 : null
  const bridgeExpiresAt = nowMs + AUTH_BRIDGE_TTL_MS
  const expiresAt = tokenExpiresAt != null ? Math.min(bridgeExpiresAt, tokenExpiresAt) : bridgeExpiresAt

  if (expiresAt <= nowMs) {
    return null
  }

  return {
    version: AUTH_BRIDGE_VERSION,
    token,
    profile: profile || null,
    expiresAt,
    updatedAt: nowMs,
  }
}

const writeBridgeAuth = (token: string | null, profile: PortalProfile | null, nowMs = Date.now()) => {
  const bridge = createBridgePayload(token, profile, nowMs)
  if (!bridge) {
    clearBridgeAuth()
    return null
  }
  localStorage.setItem(STORAGE_BRIDGE, JSON.stringify(bridge))
  return bridge
}

const readBridgeAuth = (): AuthBridgePayload | null => {
  const raw = localStorage.getItem(STORAGE_BRIDGE)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AuthBridgePayload> | null
    if (
      !parsed ||
      parsed.version !== AUTH_BRIDGE_VERSION ||
      typeof parsed.token !== 'string' ||
      !parsed.token.trim() ||
      typeof parsed.expiresAt !== 'number' ||
      typeof parsed.updatedAt !== 'number'
    ) {
      clearBridgeAuth()
      return null
    }

    if (parsed.expiresAt <= Date.now() || isTokenExpired(parsed.token)) {
      clearBridgeAuth()
      return null
    }

    return {
      version: parsed.version,
      token: parsed.token,
      profile: parsed.profile ? (parsed.profile as PortalProfile) : null,
      expiresAt: parsed.expiresAt,
      updatedAt: parsed.updatedAt,
    }
  } catch {
    clearBridgeAuth()
    return null
  }
}

const readInitialAuthState = (): AuthState => {
  const sessionToken = readToken()
  const sessionProfile = readProfile()
  if (sessionToken) {
    return {
      token: sessionToken,
      profile: sessionProfile,
    }
  }

  const bridge = readBridgeAuth()
  if (!bridge) {
    clearSessionAuth()
    return {
      token: null,
      profile: null,
    }
  }

  writeSessionAuth(bridge.token, bridge.profile)
  return {
    token: bridge.token,
    profile: bridge.profile,
  }
}

const areProfilesEqual = (left: PortalProfile | null, right: PortalProfile | null) =>
  JSON.stringify(left || null) === JSON.stringify(right || null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(() => readInitialAuthState())
  const { token, profile } = authState

  const setAuth = useCallback((nextToken: string | null, nextProfile: PortalProfile | null) => {
    writeSessionAuth(nextToken, nextProfile)
    writeBridgeAuth(nextToken, nextProfile)
    setAuthState({
      token: nextToken,
      profile: nextProfile,
    })
  }, [])

  const clearAuth = useCallback(() => {
    clearStoredAuth()
    clearApiCache()
    setAuthState({
      token: null,
      profile: null,
    })
  }, [])

  useEffect(() => setAuthRejectedHandler(clearAuth), [clearAuth])

  useEffect(() => {
    if (!token) {
      return undefined
    }

    const refreshBridge = () => {
      writeBridgeAuth(token, profile)
    }

    refreshBridge()

    const intervalId = window.setInterval(refreshBridge, AUTH_BRIDGE_HEARTBEAT_MS)
    const handleFocus = () => {
      refreshBridge()
    }
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshBridge()
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [profile, token])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_BRIDGE) {
        return
      }

      if (!event.newValue) {
        if (token) {
          clearSessionAuth()
          clearApiCache()
          setAuthState({
            token: null,
            profile: null,
          })
        }
        return
      }

      const bridge = readBridgeAuth()
      if (!bridge) {
        if (token) {
          clearSessionAuth()
          clearApiCache()
          setAuthState({
            token: null,
            profile: null,
          })
        }
        return
      }

      if (bridge.token === token && areProfilesEqual(bridge.profile, profile)) {
        return
      }

      writeSessionAuth(bridge.token, bridge.profile)
      setAuthState({
        token: bridge.token,
        profile: bridge.profile,
      })
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [profile, token])

  useEffect(() => {
    if (!token) {
      return undefined
    }

    const payload = decodeJwtPayload(token)
    if (!payload || typeof payload.exp !== 'number') {
      return undefined
    }

    const msUntilExpiry = payload.exp * 1000 - Date.now()
    if (msUntilExpiry <= 0) {
      const timeoutId = window.setTimeout(clearAuth, 0)
      return () => window.clearTimeout(timeoutId)
    }

    const timeoutId = window.setTimeout(clearAuth, msUntilExpiry)
    return () => window.clearTimeout(timeoutId)
  }, [clearAuth, token])

  useEffect(() => {
    configureClientErrorReporting({ token })
    syncTelemetryUser(profile)
  }, [profile, token])

  const value = useMemo(
    () => ({
      token,
      profile,
      setAuth,
      clearAuth,
    }),
    [clearAuth, profile, setAuth, token]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
